// fmxkit.jsx — FMX product UI primitives for the design-system restyle.
//
// React port of design_handoff_fmx_design_system/prototype/fmxkit.jsx,
// styled from the canonical T.* tokens in src/theme.js (no CSS framework,
// inline styles per codebase convention). These are the shared shell +
// control primitives the restyled screens build on: Button, IconButton,
// Badge, Meter, Tabs, FilterChip, ContentHeader, TopBar. (The app sidebar
// lives in MigSidebar.jsx — design_handoff_customer_tools.)
//
// Icons use Font Awesome 6 (`fa-solid`), loaded via CDN in public/index.html.

import { Fragment } from "react";
import { T } from "../theme";

// ---- Button ----
const BTN_VARIANTS = {
  primary:     { background: T.brand, color: T.white, boxShadow: T.shadowSm },
  secondary:   { background: T.white, color: T.brand, border: `1px solid ${T.brand}` },
  tertiary:    { background: "transparent", color: T.brand },
  neutral:     { background: T.white, color: T.ink, border: `1px solid ${T.border}` },
  destructive: { background: T.red500, color: T.white },
};

export function Button({ variant = "primary", size = "md", icon, iconAfter, children, onClick, disabled, type = "button", style = {} }) {
  const base = {
    height: 32, padding: "0 12px", border: 0, borderRadius: T.radiusBtn,
    font: `700 14px/20px ${T.fontSans}`, cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
    whiteSpace: "nowrap", transition: "background 140ms ease-out, color 140ms ease-out",
    opacity: disabled ? 0.55 : 1,
  };
  const sizeStyle = size === "sm" ? { height: 28, padding: "0 10px", fontSize: 13 }
    : size === "lg" ? { height: 40, padding: "0 18px", fontSize: 16 } : {};
  return (
    <button type={type} disabled={disabled} onClick={onClick}
      style={{ ...base, ...BTN_VARIANTS[variant], ...sizeStyle, ...style }}>
      {icon && <i className={`fa-solid fa-${icon}`} style={{ fontSize: 12 }} />}
      {children}
      {iconAfter && <i className={`fa-solid fa-${iconAfter}`} style={{ fontSize: 12 }} />}
    </button>
  );
}

// ---- IconButton ----
const ICONBTN_THEMES = {
  default: { color: T.inkMuted },
  brand:   { color: T.brand },
  danger:  { color: T.red500 },
};

export function IconButton({ icon, title, theme = "default", onClick, disabled, style = {} }) {
  return (
    <button title={title} onClick={onClick} disabled={disabled} style={{
      width: 28, height: 28, borderRadius: T.radiusBtn, border: 0, background: "transparent",
      cursor: disabled ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center",
      justifyContent: "center", opacity: disabled ? 0.55 : 1, ...ICONBTN_THEMES[theme], ...style,
    }}>
      <i className={`fa-solid fa-${icon}`} style={{ fontSize: 13 }} />
    </button>
  );
}

// ---- Badge ----
// Pill, height 20, 700 12px, pastel fill + ink text (badge palette in theme).
const BADGE_FILLS = T.badge;

export function Badge({ color = "gray", icon, children, style = {} }) {
  return (
    <span style={{
      background: BADGE_FILLS[color] || BADGE_FILLS.gray, color: T.ink,
      borderRadius: T.radiusPill, padding: "0 8px", height: 20,
      display: "inline-flex", alignItems: "center", gap: 5,
      font: `700 12px/20px ${T.fontSans}`, whiteSpace: "nowrap", ...style,
    }}>
      {icon && <i className={`fa-solid fa-${icon}`} style={{ fontSize: 9 }} />}
      {children}
    </span>
  );
}

// ---- Meter (thin progress bar) ----
export function Meter({ pct, h = 8, bg = T.blue100, fill = T.gradientProgress, style = {} }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  return (
    <div style={{ height: h, borderRadius: T.radiusPill, background: bg, overflow: "hidden", ...style }}>
      <div style={{ height: "100%", width: `${clamped}%`, background: fill, borderRadius: T.radiusPill }} />
    </div>
  );
}

// ---- Tabs ----
export function Tabs({ tabs, current, onChange, style = {} }) {
  return (
    <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, ...style }}>
      {tabs.map((t) => {
        const active = t.id === current;
        return (
          <div key={t.id} onClick={() => onChange && onChange(t.id)} style={{
            padding: "10px 16px", font: `600 14px/20px ${T.fontSans}`,
            color: active ? T.brand : T.inkMuted,
            borderBottom: `2px solid ${active ? T.brand : "transparent"}`,
            marginBottom: -1, cursor: "pointer", display: "flex", gap: 6, alignItems: "center",
          }}>
            {t.label}
            {t.count !== undefined && (
              <span style={{ color: T.inkSoft, fontWeight: 500 }}>{t.count}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---- FilterChip ----
export function FilterChip({ icon, label, value, onClick, style = {} }) {
  return (
    <span onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6, background: T.white,
      border: `1px solid ${T.border}`, borderRadius: T.radiusPill, padding: "0 10px", height: 28,
      font: `500 13px/20px ${T.fontSans}`, color: T.ink, cursor: onClick ? "pointer" : "default", ...style,
    }}>
      {icon && <i className={`fa-solid fa-${icon}`} style={{ color: T.inkMuted, fontSize: 11 }} />}
      <span style={{ color: T.inkMuted }}>{label}:</span>
      <b style={{ color: T.ink }}>{value}</b>
    </span>
  );
}

// ---- ContentHeader ----
export function ContentHeader({ title, subtitle, crumbs = [], actions }) {
  return (
    <div style={{ padding: "18px 24px 16px", borderBottom: `1px solid ${T.borderSubtle}`, background: T.white }}>
      {crumbs.length > 0 && (
        <div style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, marginBottom: 6, display: "flex", gap: 6, alignItems: "center" }}>
          {crumbs.map((c, i) => (
            <Fragment key={i}>
              {i > 0 && <i className="fa-solid fa-chevron-right" style={{ fontSize: 9 }} />}
              <span style={i === crumbs.length - 1 ? { color: T.ink } : undefined}>{c}</span>
            </Fragment>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h2 style={{ font: `700 23px/30px ${T.fontSans}`, color: T.ink, margin: 0, letterSpacing: "-0.01em" }}>{title}</h2>
          {subtitle && <div style={{ font: `400 14px/20px ${T.fontSans}`, color: T.inkMuted, marginTop: 3 }}>{subtitle}</div>}
        </div>
        {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

// ---- TopBar ----
export function TopBar({ context = "FMX Implementation", searchValue, onSearch, searchPlaceholder = "Search projects, entities, files…", rightSlot }) {
  return (
    <div style={{ height: 50, background: T.white, borderBottom: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", padding: "0 20px", gap: 12, flexShrink: 0 }}>
      <div style={{ position: "relative", width: 320 }}>
        <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 11, top: 9, color: T.inkSoft, fontSize: 13 }} />
        <input
          value={searchValue}
          onChange={onSearch ? (e) => onSearch(e.target.value) : undefined}
          placeholder={searchPlaceholder}
          style={{
            width: "100%", height: 32, padding: "0 12px 0 32px", border: `1px solid ${T.border}`,
            borderRadius: T.radiusBtn, font: `400 14px/20px ${T.fontSans}`, background: T.bg,
            boxSizing: "border-box", outline: "none",
          }}
        />
      </div>
      <div style={{ flex: 1 }} />
      <IconButton icon="circle-question" title="Help" />
      <IconButton icon="bell" title="Notifications" />
      <div style={{ width: 1, height: 22, background: T.borderSubtle, margin: "0 4px" }} />
      <div style={{ font: `500 13px/20px ${T.fontSans}`, color: T.inkMuted, display: "flex", alignItems: "center", gap: 6 }}>
        <i className="fa-solid fa-building" style={{ fontSize: 12 }} />
        {context}
      </div>
      {rightSlot && (
        <>
          <div style={{ width: 1, height: 22, background: T.borderSubtle, margin: "0 4px" }} />
          {rightSlot}
        </>
      )}
    </div>
  );
}

// ---- StepIndicator ----
// Shared wizard step bar: 24px circles (done green+check / current teal /
// upcoming white ring), bold current label, 40×2px connectors (green when
// passed). Steps at or before the current step are clickable.
export function StepIndicator({ steps, current = 1, maxStep, onStep }) {
  const reachable = maxStep ?? current;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, padding: "12px 24px", background: T.white, borderBottom: `1px solid ${T.borderSubtle}`, overflowX: "auto" }}>
      {steps.map((s, i) => {
        const n = i + 1;
        const done = n < current;
        const cur = n === current;
        const clickable = onStep && n <= reachable && !cur;
        return (
          <Fragment key={s}>
            <div onClick={() => clickable && onStep(n)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: clickable ? "pointer" : "default", whiteSpace: "nowrap" }}>
              <span style={{
                width: 24, height: 24, borderRadius: T.radiusPill, flexShrink: 0,
                background: done ? T.green500 : cur ? T.brand : T.white,
                border: done || cur ? "none" : `1.5px solid ${T.border}`,
                color: done || cur ? T.white : T.inkSoft,
                display: "inline-flex", alignItems: "center", justifyContent: "center", font: `700 12px ${T.fontSans}`,
              }}>{done ? <i className="fa-solid fa-check" style={{ fontSize: 11 }} /> : n}</span>
              <span style={{ font: cur ? `700 14px ${T.fontSans}` : `500 14px ${T.fontSans}`, color: cur ? T.ink : done ? T.inkMuted : T.inkSoft }}>{s}</span>
            </div>
            {i < steps.length - 1 && <span style={{ width: 40, height: 2, background: done ? T.green500 : T.borderSubtle, margin: "0 14px" }} />}
          </Fragment>
        );
      })}
    </div>
  );
}

// ---- AppShell ----
// Full-viewport flex layout: navy sidebar (left, see MigSidebar.jsx) + a
// column with the TopBar and a scrolling content region. `children` is the
// routed content.
export function AppShell({ sidebar, topBar, children }) {
  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: T.bg, color: T.ink, font: `400 14px/20px ${T.fontSans}`, position: "relative" }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body, #root { margin: 0; height: 100%; }
        body { font-family: ${T.fontSans}; }
      `}</style>
      {sidebar}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, position: "relative" }}>
        {topBar}
        {children}
      </div>
    </div>
  );
}
